import { renderAndHighlightTemplate } from "../../services/promptService";
import {
  escapeHtml,
  formatHtml,
  formatTokenCount,
  JINJA_VARIABLE_REGEX,
} from "./previewMisc";

const SPECIAL_VARIABLES = [
  "transcript",
  "content",
  "human_identifier",
  "memory",
];

export const updateFilesWithVariables = async (
  processedFiles,
  updatedVars,
  sortedPromptNames,
  promptOutputs,
  calculateTokenCount,
  fragmentMap
) => {
  const variables = updatedVars || {};

  for (let i = 0; i < processedFiles.preprocessing.length; i++) {
    processedFiles.preprocessing[i] = await updateFileVariableReport(
      processedFiles.preprocessing[i],
      sortedPromptNames,
      promptOutputs,
      variables,
      processedFiles,
      fragmentMap
    );

    const filename = processedFiles.preprocessing[i].path.split("/").pop();
    if (!promptOutputs[filename]) {
      promptOutputs[filename] = processedFiles.preprocessing[i].plainText;
    }
  }

  for (let i = 0; i < processedFiles.primary.length; i++) {
    processedFiles.primary[i] = await updateFileVariableReport(
      processedFiles.primary[i],
      sortedPromptNames,
      promptOutputs,
      variables,
      processedFiles,
      fragmentMap
    );
  }

  for (let i = 0; i < processedFiles.postprocessing.length; i++) {
    processedFiles.postprocessing[i] = await updateFileVariableReport(
      processedFiles.postprocessing[i],
      sortedPromptNames,
      promptOutputs,
      variables,
      processedFiles,
      fragmentMap
    );
  }

  for (let i = 0; i < processedFiles.voicemail.length; i++) {
    processedFiles.voicemail[i] = await updateFileVariableReport(
      processedFiles.voicemail[i],
      sortedPromptNames,
      promptOutputs,
      variables,
      processedFiles,
      fragmentMap
    );
  }

  for (const section of ["primary", "postprocessing", "voicemail"]) {
    for (let i = 0; i < processedFiles[section].length; i++) {
      const file = processedFiles[section][i];

      const enhancedPromptOutputs = { ...promptOutputs };
      processedFiles.preprocessing.forEach((f) => {
        const fName = f.path.split("/").pop();
        if (!enhancedPromptOutputs[fName]) {
          enhancedPromptOutputs[fName] = f.plainText || "";
        }
      });

      processedFiles[section][i].tokenCount = await calculateTokenCount(
        processedFiles[section][i].plainText
      );

      processedFiles[section][i].formattedTokenCount = formatTokenCount(
        processedFiles[section][i].tokenCount
      );
    }
  }
};

export const extractVariables = (fileContent) => {
  const variablesInContent = [];
  const variableMatches = [...fileContent.matchAll(JINJA_VARIABLE_REGEX)];

  variableMatches.forEach((match) => {
    let extractedVarName = "";
    if (match[1] !== undefined) {
      extractedVarName = match[1].trim();
    } else if (match[2] !== undefined) {
      extractedVarName = match[2].trim();
    } else if (match[3] !== undefined) {
      extractedVarName = match[3].trim();
    }
    if (!variablesInContent.includes(extractedVarName)) {
      variablesInContent.push(extractedVarName);
    }
  });

  return variablesInContent;
};

export const updateFileVariableReport = async (
  file,
  allPromptNames,
  promptOutputs,
  configuredVariables,
  processedFiles,
  fragmentMap
) => {
  const updatedFile = { ...file };
  const variablesInFile = [...file.variables];
  const configuredVarsInFile = [];
  const isPreProcessingPrompt = file.path.includes("prompt/pre-processing/");
  let usedVars = {};
  let usedPromptOutputs = {};
  let unconfiguredVars = [];
  let specialVars = [];
  const enhancedPromptOutputs = { ...promptOutputs };

  processedFiles.preprocessing.forEach((f) => {
    const fName = f.path.split("/").pop();
    if (f.path < file.path && !enhancedPromptOutputs[fName]) {
      enhancedPromptOutputs[fName] = f.plainText || "";
    }
  });

  const tools = file.promptInfo?.tools || [];

  const res = await renderAndHighlightTemplate(
    file.richText,
    configuredVariables,
    enhancedPromptOutputs,
    tools,
    true
  );
  updatedFile.richText = res.rendered_prompt;
  usedVars = res.used_variables || {};
  usedPromptOutputs = res.used_prompt_outputs || {};
  unconfiguredVars = res.unconfigured_variables || [];
  specialVars = res.special_variables || [];

  updatedFile.plainText = (
    await renderAndHighlightTemplate(
      file.plainText,
      configuredVariables,
      enhancedPromptOutputs,
      tools,
      false
    )
  ).rendered_prompt;

  variablesInFile.forEach((variableName) => {
    if (usedVars[variableName] !== undefined) {
      if (
        usedVars[variableName] &&
        typeof usedVars[variableName] === "object" &&
        "variable" in usedVars[variableName]
      ) {
        configuredVarsInFile.push({
          name: variableName,
          source: "SetVariable",
          value: usedVars[variableName].variable,
        });
        return;
      }
      configuredVarsInFile.push({
        name: variableName,
        source: "config",
        value: usedVars[variableName],
      });
      return;
    }

    if (usedPromptOutputs[variableName] !== undefined) {
      const value = usedPromptOutputs[variableName];
      const displayValue =
        typeof value === "string"
          ? value.substring(0, 50) + (value.length > 50 ? "..." : "")
          : JSON.stringify(value).substring(0, 50) + "...";

      configuredVarsInFile.push({
        name: variableName,
        source: "prompt",
        value: displayValue,
      });
      return;
    }

    if (isPreProcessingPrompt && allPromptNames.includes(variableName)) {
      let referencedFile = null;
      let referencedSection = null;

      for (const section of ["preprocessing", "primary", "postprocessing"]) {
        for (const f of processedFiles[section]) {
          const fName = f.path.split("/").pop();
          if (fName === variableName) {
            referencedFile = f;
            referencedSection = section;
            break;
          }
        }
        if (referencedFile) break;
      }

      if (
        referencedFile &&
        referencedSection === "preprocessing" &&
        referencedFile.path < file.path
      ) {
        configuredVarsInFile.push({
          name: variableName,
          source: "prompt",
          value:
            referencedFile.plainText?.substring(0, 50) +
              (referencedFile.plainText?.length > 50 ? "..." : "") ||
            "[Content from prompt]",
        });
        return;
      }
    }

    if (allPromptNames.includes(variableName)) {
      const promptPaths = {};
      allPromptNames.forEach((name) => {
        let path = "";
        ["preprocessing", "primary", "postprocessing"].forEach((section) => {
          processedFiles[section].forEach((f) => {
            if (f.path.split("/").pop() === name) {
              path = f.path;
            }
          });
        });
        promptPaths[name] = path;
      });

      if (promptPaths[variableName] && promptPaths[variableName] < file.path) {
        configuredVarsInFile.push({
          name: variableName,
          source: "future-prompt",
          value: "[Will be available from prompt]",
        });
        return;
      }
    }

    if (specialVars.includes(variableName)) {
      configuredVarsInFile.push({
        name: variableName,
        source: "special",
        value: variableName,
      });
    }
  });

  const generateReportItems = () => {
    const configItems = configuredVarsInFile.map((v) => {
      if (v.source === "config") {
        return `${formatHtml.span(
          v.value ? "success-text" : "warning-text",
          `✓ ${v.name}`
        )} - Set in config to: "${escapeHtml(v.value)}"`;
      } else if (v.source === "prompt") {
        return `${formatHtml.span(
          "success-text",
          `✓ ${v.name}`
        )} - ${formatHtml.span(
          "prompt-output-var",
          "Set from prompt output"
        )}: "${escapeHtml(v.value)}"`;
      } else if (v.source === "fragment") {
        return `${formatHtml.span(
          "success-text",
          `✓ ${v.name}`
        )} - ${formatHtml.span("fragment-var", "Used in fragments")}`;
      } else if (v.source === "SetVariable") {
        return `${formatHtml.span(
          "success-text",
          `✓ ${v.name}`
        )} - ${formatHtml.span(
          "prompt-output-var",
          "Set from SetVariable"
        )}: "${escapeHtml(v.value)}"`;
      } else {
        return `${formatHtml.span(
          "success-text",
          `✓ ${v.name}`
        )} - ${formatHtml.span(
          "future-prompt-var",
          "Will be available from prompt"
        )}`;
      }
    });

    const unconfigItems = unconfiguredVars.map(
      (name) =>
        `${formatHtml.span(
          "error-text",
          `✗ ${name}`
        )} - <strong>Not configured!</strong> This variable is not defined in config or available from any prompt`
    );

    return [...configItems, ...unconfigItems];
  };

  if (configuredVarsInFile.length === 0) {
    updatedFile.variableReport = "<i>No variables used in this template</i>";
  } else {
    const reportItems = generateReportItems();
    updatedFile.variableReport = formatHtml.ul(reportItems);
  }

  updatedFile.configuredVariables = configuredVarsInFile.map((v) => v.name);
  updatedFile.unconfiguredVariables = unconfiguredVars;

  return updatedFile;
};

export const collectVariableInfo = (
  processedFiles,
  updatedVars,
  fragmentMap
) => {
  const allVariablesInContent = [];
  const unconfiguredVariables = [];

  const extractFragmentVariables = (fragment) => {
    if (!fragment || !fragment.content) return [];

    const variablesInFragment = [];
    const variableMatches = [
      ...fragment.content.matchAll(JINJA_VARIABLE_REGEX),
    ];

    variableMatches.forEach((match) => {
      let extractedVarName = "";
      if (match[1] !== undefined) {
        extractedVarName = match[1].trim();
      } else if (match[2] !== undefined) {
        extractedVarName = match[2].trim();
      } else if (match[3] !== undefined) {
        extractedVarName = match[3].trim();
      }
      if (!variablesInFragment.includes(extractedVarName)) {
        variablesInFragment.push(extractedVarName);
      }
    });

    return variablesInFragment;
  };

  const fragmentVariables = [];
  if (fragmentMap) {
    Object.values(fragmentMap).forEach((fragment) => {
      const varsInFragment = extractFragmentVariables(fragment);
      varsInFragment.forEach((variable) => {
        if (!fragmentVariables.includes(variable)) {
          fragmentVariables.push(variable);
        }
      });
    });
  }

  fragmentVariables.forEach((variable) => {
    if (!allVariablesInContent.includes(variable)) {
      allVariablesInContent.push(variable);
    }
  });

  Object.values(processedFiles)
    .flat()
    .forEach((file) => {
      file.variables.forEach((variable) => {
        if (!allVariablesInContent.includes(variable)) {
          allVariablesInContent.push(variable);
        }
      });

      if (file.unconfiguredVariables) {
        file.unconfiguredVariables.forEach((variable) => {
          if (
            !unconfiguredVariables.includes(variable) &&
            !SPECIAL_VARIABLES.includes(variable)
          ) {
            unconfiguredVariables.push(variable);
          }
        });
      }
    });

  const configuredVariables = updatedVars || {};
  const unusedVariables = Object.keys(configuredVariables)
    .filter((varName) => !allVariablesInContent.includes(varName))
    .map((varName) => {
      if (
        configuredVariables[varName] &&
        typeof configuredVariables[varName] === "object" &&
        "variable" in configuredVariables[varName]
      ) {
        configuredVariables[varName] = configuredVariables[varName].variable;
      }
      const description = `Declared but unused variable: "${configuredVariables[varName]}"`;
      return `${formatHtml.span(
        "warning-text",
        `${varName}`
      )} - ${description}`;
    });

  return {
    allVariablesInContent,
    unconfiguredVariables,
    unusedVariables,
    configuredVariables,
    fragmentVariables,
  };
};

export const generateVariablesSection = (variableInfo) => {
  const {
    unconfiguredVariables,
    configuredVariables,
    unusedVariables,
    promptOutputs = {},
    requiredVariables = [],
  } = variableInfo;

  const title = formatHtml.h1("Variables Summary");
  const sections = [];

  sections.push(
    `${formatHtml.h3("Required Variables")}
      ${formatHtml.ul(
        requiredVariables.length > 0
          ? requiredVariables.map((varName) =>
              formatHtml.span("required-var", varName)
            )
          : [formatHtml.p("<i>No required variables detected</i>")]
      )}`
  );

  if (Object.keys(configuredVariables).length > 0) {
    const configuredList = Object.keys(configuredVariables).map((key) => {
      if (
        configuredVariables[key] &&
        typeof configuredVariables[key] === "object" &&
        "skill" in configuredVariables[key]
      ) {
        configuredVariables[key] = configuredVariables[key].variable;
      }

      const valuePreview =
        typeof configuredVariables[key] === "string"
          ? configuredVariables[key].length > 100
            ? configuredVariables[key].substring(0, 100) + "..."
            : configuredVariables[key]
          : (() => {
              const jsonString = JSON.stringify(configuredVariables[key]);
              return jsonString.length > 100
                ? jsonString.substring(0, 100) + "..."
                : jsonString;
            })();

      if (valuePreview && valuePreview !== "null" && valuePreview !== "false") {
        return `${formatHtml.span("configured-var", key)}: "${escapeHtml(
          valuePreview
        )}"`;
      }

      return `${formatHtml.span("warning-text", key)}: "${escapeHtml(
        valuePreview
      )}"`;
    });

    const sortedConfiguredList = configuredList.sort();

    sections.push(
      `${formatHtml.h3("Configured Variables")}
        ${formatHtml.ul(sortedConfiguredList)}`
    );
  } else {
    sections.push(
      `${formatHtml.p("<i>No variables configured in config file</i>")}`
    );
  }

  if (Object.keys(promptOutputs).length > 0) {
    const outputList = Object.keys(promptOutputs).map((key) => {
      const valuePreview =
        typeof promptOutputs[key] === "string"
          ? promptOutputs[key].length > 100
            ? promptOutputs[key].substring(0, 100) + "..."
            : promptOutputs[key]
          : JSON.stringify(promptOutputs[key]).substring(0, 100) + "...";

      return `${formatHtml.span("prompt-output-var", key)}: "${escapeHtml(
        valuePreview
      )}"`;
    });

    sections.push(
      `${formatHtml.h3("Variables from Prompt Outputs")}
       ${formatHtml.ul(outputList)}`
    );
  }

  if (unconfiguredVariables.length > 0) {
    const unconfiguredList = unconfiguredVariables.map((varName) =>
      formatHtml.span("error-text", varName)
    );

    sections.push(
      `<div class="error-section">
        ${formatHtml.h3("❌ Unconfigured Variables")}
        ${formatHtml.p(
          "The following variables are used in templates but not configured:"
        )}
        ${formatHtml.ul(unconfiguredList)}
      </div>`
    );
  }

  if (unusedVariables.length > 0) {
    const unusedList = unusedVariables.map((varItem) => {
      if (varItem.includes('<span class="warning-text">')) {
        return varItem;
      }

      const match = varItem.match(
        /- <span class="warning-text">([^<]+)<\/span> - (.*)/
      );
      if (match) {
        const [, varName, description] = match;
        return `${formatHtml.span(
          "warning-text",
          `${varName}`
        )} - ${description}`;
      }

      return varItem;
    });

    sections.push(
      `<div class="warning-section">
        ${formatHtml.h3("⚠️ Unused Variables")}
        ${formatHtml.p(
          "The following variables are defined in the config but not used in any template:"
        )}
        ${formatHtml.ul(unusedList)}
      </div>`
    );
  }

  return `${title}\n\n${sections.join("\n\n")}`;
};
